import Component from '../abstracts/Component';
import TrackingComponent from '../components/TrackingComponent';

export default class PaginationComponent extends Component {
  get SELECTORS() {
    return {
      infiniteScrollStartBtn: '[data-infinite-scroll]',
      pageNumber: '[data-page]',
      lastPageNumber: '[data-infinite-scroll-lastpage]',
      pageLinks: '[data-page-link]',
      nextPage: '[data-page-next]',
      footer: '[data-footer]',
      seoText: '[data-seo-text]'
    };
  }

  get CLASSES() {
    return {
      hidden: '--hidden'
    }
  }

  constructor(compEl) {
    super(compEl);
    this.infiniteScrollStartBtn = this.$component.querySelector(this.SELECTORS.infiniteScrollStartBtn);
    this.pageNumber = this.$component.querySelector(this.SELECTORS.pageNumber).dataset.page;
    this.lastPageNumber = this.$component.querySelector(this.SELECTORS.lastPageNumber)?.dataset.infiniteScrollLastpage;
    this.footer = document.querySelector(this.SELECTORS.footer);
    this.pageLinks = this.$component.querySelectorAll(this.SELECTORS.pageLinks);
    this.nextPage = this.$component.querySelector(this.SELECTORS.nextPage);
    this.seoText = document.querySelector(this.SELECTORS.seoText);
    this.seoTextHeight = null;
    if (this.seoText) {
      this.seoTextHeight = this.seoText.scrollHeight;
    }
    this.trackingComponent = new TrackingComponent();

    this.goToPageFn = this.goToPage;
  }

  /**
   * Navigates to a specified page and emits a pagination event.
   *
   * @param {HTMLElement} link - The DOM element containing the page link data.
   * @fires PAGINATION_EVENTS.goTo - Emits a pagination event with the new page number.
   */
  goToPage(link) {
    this.pageNumber = link.dataset.pageLink;
    this.nextPage.setAttribute('data-page-link', this.pageNumber + 1);
    this.$emit(this.$customEvents.PAGINATION_EVENTS.goTo, this.pageNumber);
  }

  /**
   * @function observeScroll
   * @description
   * Observes the intersection with the footer element. When the intersection ratio
   * is greater than 0 (i.e. the footer is visible), and the page number is less
   * than the last page number, emits an event to load more products.
   * @param {IntersectionObserver} observer - the intersection observer instance
   * @param {IntersectionObserverEntry[]} entries - the intersection observer entries
   */
  observeScroll() {
    const callbackFunction = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0 && (parseInt(this.pageNumber) < parseInt(this.lastPageNumber))) {
          this.pageNumber = parseInt(this.pageNumber) + 1;
          this.$emit(this.$customEvents.INFINITE_SCROLL_EVENTS.next, {page: this.pageNumber});
          observer.unobserve(entry.target);
        }
      });
    };

    let rootMargin = '400px 0px';
    if (this.seoTextHeight) {
      rootMargin = `${this.seoTextHeight}px 0px`;
    }
    const options = {
      root: null,
      rootMargin: rootMargin,
      threshold: 0.01
    };
    this.scrollObserver = new IntersectionObserver(callbackFunction, options);

    this.scrollObserver.observe(this.footer);
  }

  registerButtonListener() {
    this.infiniteScrollStartBtn && this.infiniteScrollStartBtn.addEventListener('click', () => {
      this.infiniteScrollStartBtn.classList.add(this.CLASSES.hidden);
      this.$emit(this.$customEvents.INFINITE_SCROLL_EVENTS.next, {page: this.pageNumber});
    });
  }

  updateButton(updatedButton) {
    this.$component.innerHTML = updatedButton.outerHTML;
    this.infiniteScrollStartBtn = this.$component.querySelector(this.SELECTORS.infiniteScrollStartBtn);
    this.lastPageNumber = this.infiniteScrollStartBtn.dataset.infiniteScrollLastpage;
    this.registerButtonListener();
  }

  /**
   * @function bindEvents
   * @description
   * Binds various event listeners to handle pagination interactions. Listens for
   * clicks on the "View More" button to trigger the infinite scroll event and
   * observe scroll behavior. Also listens for a custom event to hide the button
   * and update the current page number. Additionally, binds click events to page
   * links to update the page number and emit a pagination event.
   */
  bindEvents() {
    this.registerButtonListener();

    this.$on(this.$customEvents.INFINITE_SCROLL_EVENTS.loaded, () => {
      if (this.pageNumber < this.lastPageNumber) {
        this.observeScroll();
      }
    });

    this.$on(this.$customEvents.INFINITE_SCROLL_EVENTS.hide, (pageNum) => {
      this.infiniteScrollStartBtn?.classList.add(this.CLASSES.hidden);
      this.pageNumber = pageNum.payload;
      // disconnect observer if connected
      if (this.scrollObserver && this.scrollObserver.disconnect) {
        this.scrollObserver.disconnect();
      }
    });

    this.$on(this.$customEvents.INFINITE_SCROLL_EVENTS.reset, ({payload}) => {
      this.pageNumber = '1';
      this.lastPageNumber = '1';
      // disconnect observer if connected
      if (this.scrollObserver && this.scrollObserver.disconnect) {
        this.scrollObserver.disconnect();
      }
      // reactivate infinite
      if (payload.updatedElement) {
        this.updateButton(payload.updatedElement);
      }
    });

    this.pageLinks && this.pageLinks.length && this.pageLinks.forEach(link => {
      link.addEventListener('click', () => this.goToPage(link));
    });
  }

  render() {
    this.log('Rendering...');
    this.bindEvents();
  }
}
